// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { AssetLocationFilterMenu } from '@/asset/components/asset-location-filter-menu';
import { useActiveAssetCostCurrency } from '@/asset/hooks/use-active-asset-cost-currency';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { DateRangeInput } from '@/components/date-range-input';
import { EmptyState } from '@/components/empty-state';
import { PageContent } from '@/components/page-content';
import { SegmentedControl } from '@/components/segmented-control';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TooltipTypography } from '@/components/tooltip-typography';
import { type DateRangeShortcut } from '@/constants/date-range-shortcut';
import { PlayArrowRounded } from '@/constants/hardfin-standard-icons';
import { type LocationCompanyType } from '@/constants/location-company-type';
import { CurrencySelector } from '@/costs/components/currency-selector';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { BaseSearchableFilter } from '@/filters/base-searchable-filter';
import { useDownloadFile } from '@/hooks/use-download-file';
import { useItems } from '@/items/hooks/use-items';
import { type LocationFilter } from '@/location/api/get-locations';
import { type Location } from '@/location/api/types';
import { useLocations } from '@/location/hooks/use-locations';
import { DepreciationScheduleDisplayMode } from '@/report/api/types';
import { DepreciationScheduleReportLoader } from '@/report/components/depreciation-schedule-report-loader';
import { DepreciationScheduleReportTable } from '@/report/components/depreciation-schedule-report-table';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportExportButton } from '@/report/components/report-export-button';
import {
  calculateReportEstimatedTimeMs,
  depreciationScheduleReportConfig,
} from '@/report/components/report-loader';
import {
  DepreciationScheduleReportFilters,
  useDepreciationScheduleReport,
} from '@/report/hooks/use-depreciation-schedule-report';
import { useDepreciationScheduleReportEstimate } from '@/report/hooks/use-depreciation-schedule-report-estimate';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Depreciation schedule';
const IN_TRANSIT_LOCATION_FILTER_KEY = 'IN_TRANSIT';
const START_MONTH_AFTER_END_MONTH_ERROR = 'Start date cannot be after end date';
const DEPRECIATION_SCHEDULE_LOCATION_FILTER = {
  isArchived: 'all',
  isTransient: 'all',
} satisfies LocationFilter;

const DISPLAY_MODE_OPTIONS: {
  value: DepreciationScheduleDisplayMode;
  label: string;
}[] = [
  { value: 'periodDepreciation', label: 'Period depreciation' },
  { value: 'accumulatedDepreciation', label: 'Accumulated depreciation' },
  { value: 'netBookValue', label: 'Net book value' },
];

// The export renders every currency in one workbook, so the button names the
// active display mode rather than a currency, matching the rollforward report.
const EXPORT_BUTTON_LABELS: Record<DepreciationScheduleDisplayMode, string> = {
  periodDepreciation: 'Export period depreciation for all currencies',
  accumulatedDepreciation: 'Export accumulated depreciation for all currencies',
  netBookValue: 'Export net book value for all currencies',
};

const DEPRECIATION_SCHEDULE_DATE_RANGE_SHORTCUTS: DateRangeShortcut[] = [
  {
    label: 'Last 3 months',
    getDateRange: (today) => getLastFullMonths(today, 3),
  },
  {
    label: 'Last 6 months',
    getDateRange: (today) => getLastFullMonths(today, 6),
  },
  {
    label: 'Last 9 months',
    getDateRange: (today) => getLastFullMonths(today, 9),
  },
  {
    label: 'Last 12 months',
    getDateRange: (today) => getLastFullMonths(today, 12),
  },
  {
    label: 'Last 18 months',
    getDateRange: (today) => getLastFullMonths(today, 18),
  },
  {
    label: 'Last 24 months',
    getDateRange: (today) => getLastFullMonths(today, 24),
  },
];

const searchValueSchema = z.union([z.string(), z.array(z.string())]);
const searchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
  itemKeys: searchValueSchema.optional(),
  locationKeys: searchValueSchema.optional(),
  // The cost currency each run reports on, stored in the URL like the rollforward
  // reports so it persists across reloads. A code outside the app-allowed set
  // reaches the backend and is rejected there with a 400.
  currency: isoCurrencyCodeSchema.optional().catch(undefined),
});

export const Route = createFileRoute('/_app/reports/depreciation-schedule')({
  component: DepreciationScheduleRoute,
  validateSearch: searchSchema,
});

function getDefaultRange() {
  return {
    startDate: DateTime.now().minus({ months: 6 }).startOf('month'),
    endDate: DateTime.now().minus({ months: 1 }).endOf('month'),
  };
}

function getLastFullMonths(
  today: DateTime,
  months: number
): DateRange<DateTime> {
  return [
    today.minus({ months }).startOf('month'),
    today.minus({ months: 1 }).endOf('month'),
  ];
}

function normalizeSearchArray(value: string | string[] | undefined): string[] {
  if (value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function getMonthCount(startDate: DateTime, endDate: DateTime) {
  return (
    (endDate.year - startDate.year) * 12 + (endDate.month - startDate.month) + 1
  );
}

function getDateRangeError(startDate: DateTime, endDate: DateTime) {
  if (!startDate.isValid || !endDate.isValid) {
    return 'Enter valid months';
  }
  if (startDate > endDate) {
    return START_MONTH_AFTER_END_MONTH_ERROR;
  }
  if (getMonthCount(startDate, endDate) > 24) {
    return 'Select 24 months or fewer';
  }
  if (endDate.startOf('month') > DateTime.now().startOf('month')) {
    return 'End month cannot be in the future';
  }
  return undefined;
}

function getFilters(
  startDate: DateTime,
  endDate: DateTime,
  itemKeys: string[],
  locationKeys: string[],
  currencyCode: IsoCurrencyCode
): DepreciationScheduleReportFilters {
  return {
    startDate: startDate.startOf('month').toISODate() ?? '',
    endDate: endDate.endOf('month').toISODate() ?? '',
    itemKeys,
    locationKeys,
    currency: currencyCode,
  };
}

function areFiltersEqual(
  left: DepreciationScheduleReportFilters | undefined,
  right: DepreciationScheduleReportFilters
) {
  return JSON.stringify(left) === JSON.stringify(right);
}

interface LocationFilterEntry {
  id: string;
  key: string;
  customerId: string | null;
  companyType: LocationCompanyType;
}

interface LocationMenuValue {
  locationIds: string[];
  customerIds: string[];
  companyTypes: LocationCompanyType[];
}

function getLocationEntries(locations: Location[]): LocationFilterEntry[] {
  return locations.flatMap((location) => {
    const companyType: LocationCompanyType =
      location.customer === null ? 'manufacturer' : 'customer';

    return [
      {
        id: location.id,
        key: location.key,
        customerId: location.customer?.id ?? null,
        companyType,
      },
      ...location.zones.map((zone) => ({
        id: zone.id,
        key: zone.key,
        customerId: location.customer?.id ?? null,
        companyType,
      })),
    ];
  });
}

function everyEntrySelected(
  entries: LocationFilterEntry[],
  selectedKeys: Set<string>
) {
  return (
    entries.length > 0 && entries.every((entry) => selectedKeys.has(entry.key))
  );
}

function getLocationMenuValue(
  locationEntries: LocationFilterEntry[],
  locationKeys: string[]
): LocationMenuValue {
  const selectedKeys = new Set(
    locationKeys.filter((key) => key !== IN_TRANSIT_LOCATION_FILTER_KEY)
  );
  const consumedKeys = new Set<string>();
  const customerIds: string[] = [];
  const companyTypes: LocationCompanyType[] = [];

  const manufacturerEntries = locationEntries.filter(
    (entry) => entry.companyType === 'manufacturer'
  );
  if (everyEntrySelected(manufacturerEntries, selectedKeys)) {
    companyTypes.push('manufacturer');
    manufacturerEntries.forEach((entry) => consumedKeys.add(entry.key));
  }

  const customerEntries = locationEntries.filter(
    (entry) => entry.companyType === 'customer'
  );
  if (everyEntrySelected(customerEntries, selectedKeys)) {
    companyTypes.push('customer');
    customerEntries.forEach((entry) => consumedKeys.add(entry.key));
  } else {
    const customerIdsWithLocations = [
      ...new Set(
        customerEntries
          .map((entry) => entry.customerId)
          .filter((customerId): customerId is string => customerId !== null)
      ),
    ];

    customerIdsWithLocations.forEach((customerId) => {
      const entries = customerEntries.filter(
        (entry) => entry.customerId === customerId
      );
      if (everyEntrySelected(entries, selectedKeys)) {
        customerIds.push(customerId);
        entries.forEach((entry) => consumedKeys.add(entry.key));
      }
    });
  }

  return {
    locationIds: locationEntries
      .filter(
        (entry) => selectedKeys.has(entry.key) && !consumedKeys.has(entry.key)
      )
      .map((entry) => entry.id),
    customerIds,
    companyTypes,
  };
}

function DepreciationScheduleRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();
  const defaultRange = useMemo(() => getDefaultRange(), []);

  const startDate = search.start
    ? DateTime.fromISO(search.start).startOf('month')
    : defaultRange.startDate;
  const endDate = search.end
    ? DateTime.fromISO(search.end).endOf('month')
    : defaultRange.endDate;
  // Normalizing allocates for an absent or single-valued parameter, so these
  // are held: the filter menus below derive from them, and a fresh array every
  // render defeats those derivations
  const itemKeys = useMemo(
    () => normalizeSearchArray(search.itemKeys),
    [search.itemKeys]
  );
  const locationKeys = useMemo(
    () => normalizeSearchArray(search.locationKeys),
    [search.locationKeys]
  );
  const dateRangeError = getDateRangeError(startDate, endDate);

  // One cost currency per run (URL-persisted), clamped to one the org actually holds.
  const { availableCurrencies, activeCurrencyCode, isResolved } =
    useActiveAssetCostCurrency(search.currency);

  const currentFilters = getFilters(
    startDate,
    endDate,
    itemKeys,
    locationKeys,
    activeCurrencyCode
  );

  const [displayMode, setDisplayMode] =
    useState<DepreciationScheduleDisplayMode>('periodDepreciation');
  const [submittedFilters, setSubmittedFilters] =
    useState<DepreciationScheduleReportFilters>();
  const [isLoaderComplete, setIsLoaderComplete] = useState(false);
  const downloadExport = useDownloadFile();

  const report = useDepreciationScheduleReport({
    filters: submittedFilters,
  });
  // Wait for the resolved currency: an estimate under a provisional one is discarded
  // when the clamp moves it, and a Run report in that window submits a currency the
  // selector never offered.
  const reportEstimate = useDepreciationScheduleReportEstimate({
    filters:
      dateRangeError === undefined && isResolved ? currentFilters : undefined,
  });

  // Keep the URL on the currency actually rendered, so a clamped deep link is shareable.
  useEffect(() => {
    if (isResolved && search.currency !== activeCurrencyCode) {
      navigate({
        search: (previous) => ({ ...previous, currency: activeCurrencyCode }),
        replace: true,
      });
    }
  }, [activeCurrencyCode, isResolved, navigate, search.currency]);

  useEffect(() => {
    setIsLoaderComplete(false);
  }, [submittedFilters]);

  const isDirty = !areFiltersEqual(submittedFilters, currentFilters);
  const showLoader =
    report.isLoading && !isLoaderComplete && submittedFilters !== undefined;
  const canExport = report.data !== undefined && !report.isLoading;
  const exceptionAssetCount =
    report.data?.assets.filter(
      (asset) => asset.accounting.assetExceptions.length > 0
    ).length ?? 0;
  const submittedPeriodCount = submittedFilters
    ? getMonthCount(
        DateTime.fromISO(submittedFilters.startDate),
        DateTime.fromISO(submittedFilters.endDate)
      )
    : 1;
  const currentPeriodCount =
    dateRangeError === undefined ? getMonthCount(startDate, endDate) : 1;
  const estimateAssetCount = reportEstimate.data?.assetCount;
  const estimatedRunTimeLabel = useMemo(() => {
    if (dateRangeError !== undefined || reportEstimate.isError) {
      return undefined;
    }
    if (reportEstimate.isLoading) {
      return 'Estimating time...';
    }
    if (estimateAssetCount === undefined) {
      return undefined;
    }
    if (estimateAssetCount === 0) {
      return 'No matching assets';
    }

    const estimatedTimeMs = calculateReportEstimatedTimeMs(
      estimateAssetCount,
      currentPeriodCount,
      true,
      depreciationScheduleReportConfig.timing
    );
    return `Est. time: ${formatDuration(Math.ceil(estimatedTimeMs / 1000), { short: true })} (${pluralize(estimateAssetCount, { singular: 'asset' })})`;
  }, [
    currentPeriodCount,
    dateRangeError,
    estimateAssetCount,
    reportEstimate.isError,
    reportEstimate.isLoading,
  ]);

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.startOf('month').toISODate();
    const end = dateRange[1]?.endOf('month').toISODate();

    void navigate({
      search: {
        ...search,
        start,
        end,
      },
    });
  };

  const handleCurrencyChange = (currencyCode: IsoCurrencyCode) => {
    void navigate({ search: { ...search, currency: currencyCode } });
  };

  const handleRunReport = () => {
    if (dateRangeError) {
      return;
    }
    setSubmittedFilters(currentFilters);
  };

  const handleExport = () => {
    if (!submittedFilters || downloadExport.isPending) {
      return;
    }

    // Backend-generated Excel, one worksheet per cost currency, for the
    // submitted filters and the current display mode.
    const query = new URLSearchParams();
    query.set(
      'startDate',
      DateTime.fromISO(submittedFilters.startDate).toISODate() ?? ''
    );
    query.set(
      'endDate',
      DateTime.fromISO(submittedFilters.endDate).toISODate() ?? ''
    );
    for (const itemKey of submittedFilters.itemKeys) {
      query.append('itemKeys', itemKey);
    }
    for (const locationKey of submittedFilters.locationKeys) {
      query.append('locationKeys', locationKey);
    }
    query.set('displayMode', displayMode);
    downloadExport.mutate({
      path: '/v1/report/asset/depreciation-schedule/export',
      params: query,
      fallbackFilename: 'depreciation-schedule.xlsx',
    });
  };

  const handleDisplayModeChange = (mode: DepreciationScheduleDisplayMode) => {
    setDisplayMode(mode);
  };

  const apiError =
    report.isError && report.error instanceof AxiosError
      ? handleAPIError(report.error)
      : undefined;

  return (
    <PageContent
      sx={{
        gap: 2,
        flex: 1,
        display: 'grid',
        minWidth: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        gridTemplateRows: apiError
          ? 'repeat(4, auto) minmax(0, 1fr)'
          : 'repeat(3, auto) minmax(0, 1fr)',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        minWidth={0}
      >
        <Stack>
          <ReportBreadcrumbs title={REPORT_TITLE} parent="accounting" />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
        </Stack>

        <ReportExportButton
          label={
            downloadExport.isPending
              ? 'Exporting...'
              : EXPORT_BUTTON_LABELS[displayMode]
          }
          isPending={downloadExport.isPending}
          onClick={handleExport}
          disabled={!canExport}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-end"
        minWidth={0}
      >
        <SegmentedControl
          label="Show"
          value={displayMode}
          onChange={handleDisplayModeChange}
          options={DISPLAY_MODE_OPTIONS}
        />

        <Stack direction="row" spacing={2} alignItems="flex-end">
          {availableCurrencies.length > 0 && (
            <CurrencySelector
              currencies={availableCurrencies}
              value={activeCurrencyCode}
              onChange={handleCurrencyChange}
            />
          )}

          <Stack spacing={2} alignItems="end">
            <Typography variant="caption" color="text.secondary">
              Reported for{' '}
              <TooltipTypography
                variant="inherit"
                component="span"
                tooltip="Determined based on your organization's time zone setting"
              >
                {user.organization.timezone.name}
              </TooltipTypography>
            </Typography>
            <DateRangeInput
              width="200px"
              label="Date range"
              value={[startDate, endDate]}
              onChange={handleChangeDateRange}
              shortcuts={DEPRECIATION_SCHEDULE_DATE_RANGE_SHORTCUTS}
              error={dateRangeError !== undefined}
              helperText={dateRangeError}
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <SupportErrorBoundary message="Failed to load filter options.">
          <Suspense>
            <DepreciationScheduleFilters
              itemKeys={itemKeys}
              locationKeys={locationKeys}
              onChangeItemKeys={(nextItemKeys) => {
                void navigate({
                  search: {
                    ...search,
                    itemKeys:
                      nextItemKeys.length > 0 ? nextItemKeys : undefined,
                  },
                });
              }}
              onChangeLocationKeys={(nextLocationKeys) => {
                void navigate({
                  search: {
                    ...search,
                    locationKeys:
                      nextLocationKeys.length > 0
                        ? nextLocationKeys
                        : undefined,
                  },
                });
              }}
            />
          </Suspense>
        </SupportErrorBoundary>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PlayArrowRounded />}
            disabled={dateRangeError !== undefined || report.isLoading}
            onClick={handleRunReport}
          >
            Run report
          </Button>
          {estimatedRunTimeLabel && (
            <Typography variant="caption" color="text.secondary">
              {estimatedRunTimeLabel}
            </Typography>
          )}
        </Stack>
      </Stack>

      {apiError && (
        <Alert severity="error">
          {apiError.errorType === 'date_range_exceeded'
            ? 'Select 24 months or fewer'
            : apiError.message}
        </Alert>
      )}

      {showLoader ? (
        <DepreciationScheduleReportLoader
          isDataReady={!report.isLoading}
          onComplete={() => setIsLoaderComplete(true)}
          periodCount={submittedPeriodCount}
        />
      ) : report.data ? (
        <Stack spacing={1} minWidth={0} minHeight={0} overflow="hidden">
          {exceptionAssetCount > 0 ? (
            <Alert severity="warning">
              This report includes{' '}
              {pluralize(exceptionAssetCount, { singular: 'asset' })} with
              exceptions
            </Alert>
          ) : isDirty ? (
            <Alert severity="info">
              Run the report to refresh the schedule with the selected filters
            </Alert>
          ) : null}
          <DepreciationScheduleReportTable
            assets={report.data.assets}
            periods={report.data.periods}
            displayMode={displayMode}
            organizationName={user.organization.name}
            isLoading={report.isLoading}
          />
        </Stack>
      ) : (
        <Box
          border={1}
          borderColor="divider"
          borderRadius={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minWidth={0}
          minHeight={240}
        >
          <EmptyState title="Depreciation schedule report not yet run">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography color="text.secondary" textAlign="center">
                Choose months and filters, then click Run report
              </Typography>
              <PlayArrowRounded fontSize="small" color="action" />
            </Stack>
          </EmptyState>
        </Box>
      )}
      <SupportSnackbar
        isOpen={downloadExport.isError}
        onClose={() => downloadExport.reset()}
        message="An error occurred while exporting the depreciation schedule."
      />
    </PageContent>
  );
}

interface DepreciationScheduleFiltersProps {
  itemKeys: string[];
  locationKeys: string[];
  onChangeItemKeys: (itemKeys: string[]) => void;
  onChangeLocationKeys: (locationKeys: string[]) => void;
}

function DepreciationScheduleFilters(props: DepreciationScheduleFiltersProps) {
  const { items } = useItems({ archived: true, limit: 5000 });
  const { locations } = useLocations({
    filter: DEPRECIATION_SCHEDULE_LOCATION_FILTER,
  });
  // One entry per site and per zone, then a pass that folds fully selected
  // groups back into a company type. Both walk the whole location set, so
  // neither belongs in the render body.
  const locationEntries = useMemo(
    () => getLocationEntries(locations),
    [locations]
  );
  const inTransitSelected = props.locationKeys.includes(
    IN_TRANSIT_LOCATION_FILTER_KEY
  );
  const locationMenuValue = useMemo(
    () => getLocationMenuValue(locationEntries, props.locationKeys),
    [locationEntries, props.locationKeys]
  );

  const getLocationKeys = (
    locationIds: string[],
    locationCustomerIds: string[],
    locationCompanyTypes: LocationCompanyType[],
    includeInTransit: boolean
  ) => {
    const selectedLocationIds = new Set(locationIds);
    const selectedCustomerIds = new Set(locationCustomerIds);
    const selectedCompanyTypes = new Set(locationCompanyTypes);
    const nextLocationKeys = locationEntries
      .filter(
        (entry) =>
          selectedLocationIds.has(entry.id) ||
          (entry.customerId !== null &&
            selectedCustomerIds.has(entry.customerId)) ||
          selectedCompanyTypes.has(entry.companyType)
      )
      .map((entry) => entry.key);

    if (includeInTransit) {
      nextLocationKeys.push(IN_TRANSIT_LOCATION_FILTER_KEY);
    }

    return [...new Set(nextLocationKeys)];
  };

  // Memoized on the items: the options no longer carry selection, so a
  // checkbox no longer has to re-sort the whole catalogue
  const itemOptions = useMemo(
    () =>
      [...items]
        .sort((a, b) => {
          if (a.isArchived !== b.isArchived) {
            return a.isArchived ? 1 : -1;
          }
          return a.sku.localeCompare(b.sku);
        })
        .map((item) => ({
          value: item.key,
          label: `${item.sku} (${item.name})`,
          isArchived: item.isArchived,
          color: item.isArchived ? 'text.secondary' : undefined,
        })),
    [items]
  );

  const handleChangeItem = (itemKey: string, isActive: boolean) => {
    if (isActive) {
      props.onChangeItemKeys([...props.itemKeys, itemKey]);
      return;
    }

    props.onChangeItemKeys(props.itemKeys.filter((key) => key !== itemKey));
  };

  return (
    <Stack direction="row" spacing={1}>
      <BaseSearchableFilter
        label="Item"
        options={itemOptions}
        selectedValues={props.itemKeys}
        onChangeOption={handleChangeItem}
        searchPlaceholder="Search items..."
      />
      <AssetLocationFilterMenu
        label="Location"
        locationFilter={DEPRECIATION_SCHEDULE_LOCATION_FILTER}
        locationIds={locationMenuValue.locationIds}
        customerIds={locationMenuValue.customerIds}
        companyTypes={locationMenuValue.companyTypes}
        onChange={(locationIds, locationCustomerIds, locationCompanyTypes) => {
          props.onChangeLocationKeys(
            getLocationKeys(
              locationIds,
              locationCustomerIds,
              locationCompanyTypes,
              inTransitSelected
            )
          );
        }}
        extraOptions={[
          {
            id: IN_TRANSIT_LOCATION_FILTER_KEY,
            label: 'In transit',
            isActive: inTransitSelected,
            onChange: (isActive) => {
              props.onChangeLocationKeys(
                getLocationKeys(
                  locationMenuValue.locationIds,
                  locationMenuValue.customerIds,
                  locationMenuValue.companyTypes,
                  isActive
                )
              );
            },
          },
        ]}
      />
    </Stack>
  );
}
